import React, { useState } from 'react';
import './SearchBar.scss';

interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  placeholderText: string;
  locationQuery?: string;
  onLocationChange?: (value: string) => void;
  isEnhanced?: boolean;
  onEnhancedChange?: (value: boolean) => void;
  spatialOperator?: string;
  onSpatialOperatorChange?: (value: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  onSearchChange,
  onSearch,
  placeholderText,
  locationQuery = '',
  onLocationChange,
  isEnhanced = true,
  onEnhancedChange,
  spatialOperator = 'within',
  onSpatialOperatorChange,
}) => {
  // Local state for enhanced mode and spatial operator if not controlled
  const [localEnhanced, setLocalEnhanced] = useState(isEnhanced);
  const [localSpatialOperator, setLocalSpatialOperator] =
    useState(spatialOperator);
  const [localLocation, setLocalLocation] = useState(locationQuery);

  const enhanced = onEnhancedChange ? isEnhanced : localEnhanced;
  const operator = onSpatialOperatorChange
    ? spatialOperator
    : localSpatialOperator;
  const location = onLocationChange ? locationQuery : localLocation;

  const handleEnhancedToggle = () => {
    const newValue = !enhanced;
    if (onEnhancedChange) {
      onEnhancedChange(newValue);
    } else {
      setLocalEnhanced(newValue);
    }
  };

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    if (onSpatialOperatorChange) {
      onSpatialOperatorChange(newValue);
    } else {
      setLocalSpatialOperator(newValue);
    }
  };

  const handleLocationChange = (value: string) => {
    if (onLocationChange) {
      onLocationChange(value);
    } else {
      setLocalLocation(value);
    }
  };

  return (
    <div className="enhanced-search-bar">
      {/* Text search input */}
      <div className="search-field text-search">
        <input
          type="text"
          className="search-input"
          placeholder={placeholderText || 'What are you looking for?'}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch();
            }
          }}
        />
      </div>

      {/* Enhanced toggle */}
      <button
        type="button"
        className="search-field enhanced-toggle"
        onClick={handleEnhancedToggle}
        aria-pressed={enhanced}
        aria-label="Enhanced search"
        title="Enhanced search"
      >
        <span className="toggle-switch" aria-hidden="true">
          <span className="toggle-slider"></span>
        </span>
        <span className="toggle-label">Enhanced</span>
      </button>

      {/* Spatial operator dropdown */}
      <div className="search-field spatial-operator">
        <select
          className="operator-select"
          value={operator}
          onChange={handleOperatorChange}
        >
          <option value="within">Within</option>
          <option value="intersects">Intersects</option>
          <option value="contains">Contains</option>
        </select>
      </div>

      {/* Location search input */}
      <div className="search-field location-search">
        <input
          type="text"
          className="search-input"
          placeholder="Where is it located?"
          value={location}
          onChange={(e) => handleLocationChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch();
            }
          }}
        />
      </div>

      {/* Search button */}
      <button className="search-button" onClick={onSearch} aria-label="Search">
        <svg
          width="20"
          height="20"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
  );
};

export default SearchBar;
